import { AssessmentSelectDto, AssessmentType, TechnologySelectDto } from '@@types/api.types';
import { useAssessmentsQueryOptions } from '@queries/assessment.query';
import { useQuery } from '@tanstack/react-query';
import { FunctionComponent } from 'react';

type BarChartItem = {
  assessmentId?: string | null;
  customTitle?: string;
  title: string;
  valueRange: {
    max: number;
    min: number;
  };
};

type DomainGroup = {
  assessments: AssessmentSelectDto[];
  id: string;
  name: string;
};

function filterLatestAssessments(assessments: AssessmentSelectDto[]): AssessmentSelectDto[] {
  const filteredAssessments: AssessmentSelectDto[] = [];
  const assessmentsSortedByLatest = [...assessments].sort((a, b) => b.createdAt.localeCompare(a.createdAt));

  for (let i = 0; i < assessmentsSortedByLatest.length; i++) {
    const assessment = assessmentsSortedByLatest[i];
    const previousAssessment = assessmentsSortedByLatest
      .slice(0, i)
      .find((prev) => prev.use.id === assessment.use.id && prev.domain?.id === assessment.domain?.id);
    if (previousAssessment) continue;
    filteredAssessments.push(assessment);
  }

  return filteredAssessments;
}

export function computeDataGroups(params: {
  assessments: AssessmentSelectDto[];
  technology?: TechnologySelectDto;
}): {
  dataGroups: BarChartItem[][];
  domainGroups: DomainGroup[];
  filteredAssessments: AssessmentSelectDto[];
  hasData: boolean;
} {
  const { assessments, technology } = params;

  const domains = technology?.uses?.map((use) => use.domains).flat() || [];
  const domainsSet = new Set(domains.map((domain) => domain.id));
  const filteredAssessments = filterLatestAssessments(assessments);

  const domainGroups: DomainGroup[] = Array.from(domainsSet).map((domainId) => ({
    assessments: filteredAssessments.filter((assessment) => assessment.domain?.id === domainId) || [],
    id: domainId,
    name: domains.find((domain) => domain.id === domainId)?.name || '',
  }));

  const dataGroups = domainGroups.map((group) =>
    group.assessments.map((assessment) => ({
      assessmentId: assessment.id,
      customTitle: assessment.use.name || '',
      title: assessment.domain?.name || '',
      valueRange: { max: assessment.score, min: 1 },
    })),
  );

  const hasData =
    !!technology &&
    domains.length > 0 &&
    filteredAssessments.length > 0 &&
    dataGroups.length > 0 &&
    dataGroups.some((group) => group.length > 0);

  return { dataGroups, domainGroups, filteredAssessments, hasData };
}

type TrlAcrossDomainsBarChartComponentProps = {
  onItemClick?: (assessmentId: string) => void;
  technology?: TechnologySelectDto;
};

export const TrlAcrossDomainsBarChartComponent: FunctionComponent<TrlAcrossDomainsBarChartComponentProps> = (props) => {
  const { onItemClick, technology } = props;
  const backgroundMarginPixels = 10;
  const barHeightPixels = 32;
  const barSpacingPixels = 12;
  const leftMarginPixels = 15;
  const maxTrl = 9;

  const backgroundColor = '#F0F0F0';

  // New gradient style based on the SVG gradient
  const primaryGradient = 'linear-gradient(to right, #eae8fb, #c2bbf9)';
  const primaryTextColor = '#3D2EFF';
  const secondaryGradient = primaryGradient; // Using same gradient for both
  const secondaryTextColor = primaryTextColor;

  const assessmentQueryOptions = useAssessmentsQueryOptions({
    technologyId: technology?.id,
    type: [AssessmentType.TRL],
  });
  const { data: assessments = [] } = useQuery(assessmentQueryOptions);

  const { dataGroups, hasData } = computeDataGroups({ assessments, technology });

  if (!hasData) {
    return null;
  }

  const totalGridColumns = maxTrl;

  const CustomBarChartComponent = ({ items }: { items: BarChartItem[] }) => {
    if (items.length === 0) {
      return null;
    }

    const heightPixels =
      items.length * barHeightPixels + (items.length - 1) * barSpacingPixels + backgroundMarginPixels;

    const handleClick = (assessmentId?: string | null) => {
      if (onItemClick && assessmentId) {
        onItemClick(assessmentId);
      }
    };

    // Use the first item's assessmentId for the domain title click
    const firstItemAssessmentId = items[0]?.assessmentId;
    const isDomainClickable = Boolean(onItemClick && firstItemAssessmentId);

    return (
      <>
        <div
          className={`font-medium mt-4 mb-[2px] first:mt-0 text-[#1F1C1A] text-medium ${isDomainClickable ? 'cursor-pointer hover:underline' : ''}`}
          onClick={() => handleClick(firstItemAssessmentId)}
        >
          {items[0].title}
        </div>
        <div className="flex items-start">
          <div className="flex gap-1 relative rounded-md w-full">
            {Array.from({ length: totalGridColumns }).map((_, index) => (
              <div
                className="rounded-md w-full"
                key={index}
                style={{
                  backgroundColor,
                  height: `${heightPixels}px`,
                }}
              />
            ))}
            <div className="absolute h-full top-0 w-full">
              {items.map((item, index) => {
                const isClickable = Boolean(onItemClick && item.assessmentId);
                return (
                  <div
                    className="absolute rounded-r-md flex items-center overflow-visible"
                    key={item.title + index}
                    style={{
                      background: index === 0 ? primaryGradient : secondaryGradient,
                      height: `${barHeightPixels}px`,
                      left: `-${leftMarginPixels}px`,
                      top: `${index * barHeightPixels + index * barSpacingPixels + backgroundMarginPixels / 2}px`,
                      width: `calc(${(item.valueRange.max / totalGridColumns) * 100}% + 14px)`,
                    }}
                  >
                    <div
                      className={`text-black font-normal ml-4 whitespace-nowrap ${isClickable ? 'cursor-pointer hover:underline' : ''}`}
                      onClick={() => handleClick(item.assessmentId)}
                    >
                      {item.customTitle}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div
            className="bg-white shrink-0 ml-1 rounded-md"
            style={{
              height: `${heightPixels}px`,
              width: `calc((100%) / ${maxTrl} - ${barSpacingPixels}px)`,
            }}
          >
            {items.map((item, index) => (
              <div
                className="flex font-medium items-center justify-center text-center"
                key={`score-${item.title}-${index}`}
                style={{
                  color: index === 0 ? primaryTextColor : secondaryTextColor,
                  height: `${barHeightPixels}px`,
                  marginTop: index === 0 ? `${backgroundMarginPixels / 2}px` : `${barSpacingPixels}px`,
                }}
              >
                {item.valueRange.max}
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  return dataGroups.map((dataGroup, index) => (
    <CustomBarChartComponent
      items={dataGroup}
      key={index}
    />
  ));
};
